import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Checkbox } from "@/components/ui/checkbox";
import { Lightbulb, AlertCircle, CheckCircle, Info, ArrowRight, ChevronDown } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

interface ArticleContentProps {
  content: string;
}

// Enhanced markdown-like renderer with support for links, tables, blockquotes, checklists, infoboxes, CTAs, highlights, FAQs, etc.
export function ArticleContent({ content }: ArticleContentProps) {
  return <ArticleContentRenderer content={content} />;
}

function ArticleContentRenderer({ content }: ArticleContentProps) {
  const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>({});

  const toggleItem = (id: string) => {
    setCheckedItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const elements = renderContent(content, checkedItems, toggleItem);
  return <div className="article-content">{elements}</div>;
}

// Block types for special content
interface InfoboxBlock {
  type: "tip" | "warning" | "success" | "info";
  title?: string;
  content: string[];
}

interface CTABlock {
  link: string;
  text: string;
}

interface HighlightBlock {
  content: string[];
}

interface FAQItem {
  question: string;
  answer: string[];
}

function renderContent(
  content: string,
  checkedItems: Record<string, boolean>,
  toggleItem: (id: string) => void
): JSX.Element[] {
  const lines = content.split("\n");
  const elements: JSX.Element[] = [];
  let listItems: string[] = [];
  let listType: "ul" | "ol" | null = null;
  let tableRows: string[][] = [];
  let inTable = false;
  let blockquoteLines: string[] = [];
  let checklistItems: string[] = [];
  let inChecklist = false;
  let checklistKey = 0;
  
  // New block states
  let inInfobox = false;
  let infoboxType: "tip" | "warning" | "success" | "info" = "info";
  let infoboxTitle = "";
  let infoboxContent: string[] = [];
  
  let inHighlight = false;
  let highlightContent: string[] = [];
  
  let inFaq = false;
  let faqItems: FAQItem[] = [];
  let currentFaqQuestion = "";
  let currentFaqAnswer: string[] = [];
  
  // Multiline CTA block state
  let inCta = false;
  let ctaContent: string[] = [];
  
  let key = 0;

  const flushList = () => {
    if (listItems.length > 0 && listType) {
      const ListTag = listType;
      elements.push(
        <ListTag
          key={key++}
          className={
            listType === "ul"
              ? "list-none pl-0 space-y-2 my-6"
              : "list-decimal pl-6 space-y-2 my-6"
          }
        >
          {listItems.map((item, i) => (
            <li
              key={i}
              className={listType === "ul" ? "flex items-start gap-3" : "text-muted-foreground leading-relaxed"}
            >
              {listType === "ul" && (
                <span className="text-primary mt-1 flex-shrink-0">
                  {item.startsWith("✅") || item.startsWith("✓") ? (
                    <svg className="w-5 h-5 text-lp-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <span className="w-2 h-2 rounded-full bg-primary block mt-2" />
                  )}
                </span>
              )}
              <span
                className="text-muted-foreground leading-relaxed"
                dangerouslySetInnerHTML={{ __html: formatInline(item.replace(/^[✅✓]\s*/, "")) }}
              />
            </li>
          ))}
        </ListTag>
      );
      listItems = [];
      listType = null;
    }
  };

  const flushBlockquote = () => {
    if (blockquoteLines.length > 0) {
      elements.push(
        <blockquote
          key={key++}
          className="border-l-4 border-primary bg-primary/5 pl-6 py-4 my-6 italic text-muted-foreground rounded-r-lg"
        >
          {blockquoteLines.map((line, i) => (
            <p key={i} dangerouslySetInnerHTML={{ __html: formatInline(line) }} />
          ))}
        </blockquote>
      );
      blockquoteLines = [];
    }
  };

  const flushChecklist = () => {
    if (checklistItems.length > 0) {
      elements.push(
        <div key={key++} className="my-6 bg-muted/30 rounded-lg p-4 border border-border">
          <div className="space-y-3">
            {checklistItems.map((item, i) => {
              const itemId = `checklist-${checklistKey}-${i}`;
              const isChecked = checkedItems[itemId] || false;
              return (
                <label
                  key={i}
                  className="flex items-center gap-3 cursor-pointer group"
                >
                  <Checkbox
                    id={itemId}
                    checked={isChecked}
                    onCheckedChange={() => toggleItem(itemId)}
                    className="h-5 w-5"
                  />
                  <span
                    className={`text-sm transition-all ${
                      isChecked
                        ? "text-muted-foreground line-through"
                        : "text-foreground"
                    }`}
                    dangerouslySetInnerHTML={{ __html: formatInline(item) }}
                  />
                </label>
              );
            })}
          </div>
        </div>
      );
      checklistItems = [];
      checklistKey++;
      inChecklist = false;
    }
  };

  const flushInfobox = () => {
    if (infoboxContent.length > 0) {
      const iconMap = {
        tip: <Lightbulb className="w-5 h-5" />,
        warning: <AlertCircle className="w-5 h-5" />,
        success: <CheckCircle className="w-5 h-5" />,
        info: <Info className="w-5 h-5" />,
      };
      const colorMap = {
        tip: "bg-lp-yellow/10 border-lp-yellow text-lp-yellow",
        warning: "bg-orange-50 border-orange-400 text-orange-600",
        success: "bg-lp-green/10 border-lp-green text-lp-green",
        info: "bg-primary/10 border-primary text-primary",
      };
      const bgMap = {
        tip: "bg-lp-yellow/5",
        warning: "bg-orange-50",
        success: "bg-lp-green/5",
        info: "bg-primary/5",
      };

      elements.push(
        <div
          key={key++}
          className={`my-6 rounded-lg border-l-4 p-4 ${colorMap[infoboxType]} ${bgMap[infoboxType]}`}
        >
          <div className="flex items-start gap-3">
            <span className="flex-shrink-0 mt-0.5">{iconMap[infoboxType]}</span>
            <div className="flex-1">
              {infoboxTitle && (
                <p className="font-semibold text-foreground mb-1">{infoboxTitle}</p>
              )}
              {infoboxContent.map((line, i) => (
                <p
                  key={i}
                  className="text-muted-foreground text-sm leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: formatInline(line) }}
                />
              ))}
            </div>
          </div>
        </div>
      );
      infoboxContent = [];
      infoboxTitle = "";
      inInfobox = false;
    }
  };

  const flushHighlight = () => {
    if (highlightContent.length > 0) {
      elements.push(
        <div
          key={key++}
          className="my-6 bg-lp-yellow/20 border border-lp-yellow/30 rounded-lg p-4"
        >
          <div className="flex items-start gap-3">
            <span className="text-lp-yellow flex-shrink-0 mt-0.5">💡</span>
            <div className="flex-1">
              {highlightContent.map((line, i) => (
                <p
                  key={i}
                  className="text-foreground font-medium leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: formatInline(line) }}
                />
              ))}
            </div>
          </div>
        </div>
      );
      highlightContent = [];
      inHighlight = false;
    }
  };

  const flushFaqItem = () => {
    if (currentFaqQuestion && currentFaqAnswer.length > 0) {
      faqItems.push({
        question: currentFaqQuestion,
        answer: [...currentFaqAnswer],
      });
      currentFaqQuestion = "";
      currentFaqAnswer = [];
    }
  };

  const flushFaq = () => {
    flushFaqItem();
    if (faqItems.length > 0) {
      elements.push(
        <div key={key++} className="my-8">
          <h3 className="text-xl font-bold mb-4 text-foreground">Ofte stillede spørgsmål</h3>
          <Accordion type="single" collapsible className="space-y-2">
            {faqItems.map((item, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="border border-border rounded-lg px-4 bg-muted/20"
              >
                <AccordionTrigger className="text-left font-medium text-foreground hover:no-underline">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  {item.answer.map((line, j) => (
                    <p
                      key={j}
                      className="leading-relaxed mb-2 last:mb-0"
                      dangerouslySetInnerHTML={{ __html: formatInline(line) }}
                    />
                  ))}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      );
      faqItems = [];
      inFaq = false;
    }
  };

  const flushTable = () => {
    if (tableRows.length > 0) {
      const headers = tableRows[0];
      const bodyRows = tableRows.slice(1).filter(row => !row.every(cell => cell.match(/^-+$/)));
      
      elements.push(
        <div key={key++} className="my-8 overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                {headers.map((header, i) => (
                  <th
                    key={i}
                    className="px-4 py-3 text-left font-semibold text-foreground border-b border-border"
                  >
                    {header.trim()}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bodyRows.map((row, i) => (
                <tr key={i} className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
                  {row.map((cell, j) => (
                    <td
                      key={j}
                      className="px-4 py-3 text-muted-foreground"
                      dangerouslySetInnerHTML={{ __html: formatInline(cell.trim()) }}
                    />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      tableRows = [];
      inTable = false;
    }
  };

  const formatInline = (text: string): string => {
    return text
      // Bold
      .replace(/\*\*(.+?)\*\*/g, "<strong class='font-semibold text-foreground'>$1</strong>")
      // Italic
      .replace(/\*(.+?)\*/g, "<em>$1</em>")
      // Internal links: [text](/artikel/slug) or [text](/kategori/slug)
      .replace(/\[([^\]]+)\]\(\/([^)]+)\)/g, (_, linkText, path) => {
        return `<a href="/${path}" class="text-primary hover:underline font-medium">${linkText}</a>`;
      })
      // External links: [text](https://...)
      .replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, (_, linkText, url) => {
        return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-primary hover:underline font-medium">${linkText}</a>`;
      })
      // Inline code
      .replace(/`([^`]+)`/g, "<code class='bg-muted px-1.5 py-0.5 rounded text-sm font-mono'>$1</code>")
      // Arrow symbol
      .replace(/→/g, "<span class='text-primary'>→</span>");
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    // Skip empty lines but flush current elements (except inside blocks)
    if (!trimmed) {
      if (!inInfobox && !inHighlight && !inFaq && !inCta) {
        flushList();
        flushBlockquote();
        flushChecklist();
        if (inTable) flushTable();
      }
      continue;
    }

    // ===== INFOBOX BLOCK =====
    // [infobox type="tip" title="Vidste du?"]
    const infoboxMatch = trimmed.match(/^\[infobox(?:\s+type="(tip|warning|success|info)")?(?:\s+title="([^"]*)")?\]$/);
    if (infoboxMatch) {
      flushList();
      inInfobox = true;
      infoboxType = (infoboxMatch[1] as "tip" | "warning" | "success" | "info") || "info";
      infoboxTitle = infoboxMatch[2] || "";
      continue;
    }
    if (trimmed === "[/infobox]") {
      flushInfobox();
      continue;
    }
    if (inInfobox) {
      infoboxContent.push(trimmed);
      continue;
    }

    // ===== HIGHLIGHT BLOCK (single line) =====
    // [highlight]text[/highlight]
    const inlineHighlightMatch = trimmed.match(/^\[highlight\](.+)\[\/highlight\]$/);
    if (inlineHighlightMatch) {
      flushList();
      const text = inlineHighlightMatch[1];
      elements.push(
        <div
          key={key++}
          className="my-6 bg-lp-yellow/20 border border-lp-yellow/30 rounded-lg p-4"
        >
          <div className="flex items-start gap-3">
            <span className="text-lp-yellow flex-shrink-0 mt-0.5">💡</span>
            <p
              className="text-foreground font-medium leading-relaxed"
              dangerouslySetInnerHTML={{ __html: formatInline(text) }}
            />
          </div>
        </div>
      );
      continue;
    }

    // ===== HIGHLIGHT BLOCK (multiline) =====
    if (trimmed === "[highlight]") {
      flushList();
      inHighlight = true;
      continue;
    }
    if (trimmed === "[/highlight]") {
      flushHighlight();
      continue;
    }
    if (inHighlight) {
      highlightContent.push(trimmed);
      continue;
    }

    // ===== CTA BLOCK (single line) =====
    // [cta link="/artikel/slug"]Button text[/cta]
    const ctaMatch = trimmed.match(/^\[cta\s+link="([^"]+)"\](.+)\[\/cta\]$/);
    if (ctaMatch) {
      flushList();
      const [, link, text] = ctaMatch;
      elements.push(
        <div key={key++} className="my-8">
          <Link
            to={link}
            className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-lg font-semibold hover:bg-primary/90 transition-colors shadow-md hover:shadow-lg"
          >
            {text}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      );
      continue;
    }

    // ===== CTA BLOCK (multiline) =====
    // [cta] ... [/cta]
    if (trimmed === "[cta]") {
      flushList();
      inCta = true;
      ctaContent = [];
      continue;
    }
    if (trimmed === "[/cta]") {
      if (ctaContent.length > 0) {
        const text = ctaContent.join(' ');
        elements.push(
          <div key={key++} className="my-8 bg-primary/5 border border-primary/20 rounded-lg p-6">
            <p 
              className="text-foreground leading-relaxed"
              dangerouslySetInnerHTML={{ __html: formatInline(text) }}
            />
          </div>
        );
      }
      inCta = false;
      ctaContent = [];
      continue;
    }
    if (inCta) {
      ctaContent.push(trimmed);
      continue;
    }

    // ===== FAQ BLOCK =====
    if (trimmed === "[faq]") {
      flushList();
      inFaq = true;
      continue;
    }
    if (trimmed === "[/faq]") {
      flushFaq();
      continue;
    }
    if (inFaq) {
      // Question line starts with Q: or Spørgsmål:
      if (trimmed.startsWith("Q:")) {
        flushFaqItem();
        currentFaqQuestion = trimmed.slice(2).trim();
      }
      else if (trimmed.startsWith("Spørgsmål:")) {
        flushFaqItem();
        currentFaqQuestion = trimmed.slice(10).trim();
      }
      // Answer line starts with A: or Svar:
      else if (trimmed.startsWith("A:")) {
        currentFaqAnswer.push(trimmed.slice(2).trim());
      }
      else if (trimmed.startsWith("Svar:")) {
        currentFaqAnswer.push(trimmed.slice(5).trim());
      }
      // Continuation of answer
      else if (currentFaqQuestion) {
        currentFaqAnswer.push(trimmed);
      }
      continue;
    }

    // Checklist block start/end
    if (trimmed === "[checklist]") {
      flushList();
      inChecklist = true;
      continue;
    }
    if (trimmed === "[/checklist]") {
      flushChecklist();
      continue;
    }

    // Checklist items (inside [checklist] block)
    if (inChecklist && trimmed.startsWith("- [ ]")) {
      checklistItems.push(trimmed.slice(6).trim());
      continue;
    }

    // Table detection (lines starting with |)
    if (trimmed.startsWith("|") && trimmed.endsWith("|")) {
      flushList();
      flushBlockquote();
      inTable = true;
      const cells = trimmed
        .slice(1, -1)
        .split("|")
        .map(cell => cell.trim());
      tableRows.push(cells);
      continue;
    } else if (inTable) {
      flushTable();
    }

    // Blockquote
    if (trimmed.startsWith("> ")) {
      flushList();
      blockquoteLines.push(trimmed.slice(2));
      continue;
    } else if (blockquoteLines.length > 0) {
      flushBlockquote();
    }

    // Horizontal rule
    if (trimmed === "---" || trimmed === "***" || trimmed === "___") {
      flushList();
      elements.push(<hr key={key++} className="my-8 border-border" />);
      continue;
    }

    // Headers
    if (trimmed.startsWith("### ")) {
      flushList();
      elements.push(
        <h3
          key={key++}
          className="text-xl font-bold mt-10 mb-4 text-foreground flex items-center gap-2"
        >
          {trimmed.slice(4)}
        </h3>
      );
    } else if (trimmed.startsWith("## ")) {
      flushList();
      elements.push(
        <h2
          key={key++}
          className="text-2xl font-bold mt-12 mb-5 text-foreground border-b border-border pb-2"
        >
          {trimmed.slice(3)}
        </h2>
      );
    }
    // Unordered list
    else if (trimmed.startsWith("- ") || trimmed.startsWith("• ")) {
      if (listType !== "ul") {
        flushList();
        listType = "ul";
      }
      listItems.push(trimmed.slice(2));
    }
    // Emoji bullet list (✅, ✓, etc.) - NOTE: 🔗 removed as it's used for reference links
    else if (/^[✅✓🌏🇨🇦🇪🇺🇺🇸🇳🇴🛂🧾💬🧠💼🌍🧳📱]/.test(trimmed)) {
      if (listType !== "ul") {
        flushList();
        listType = "ul";
      }
      listItems.push(trimmed);
    }
    // Ordered list
    else if (/^\d+\.\s/.test(trimmed)) {
      if (listType !== "ol") {
        flushList();
        listType = "ol";
      }
      listItems.push(trimmed.replace(/^\d+\.\s/, ""));
    }
    // Paragraph
    else {
      flushList();
      elements.push(
        <p
          key={key++}
          className="text-muted-foreground leading-relaxed my-4 text-base"
          dangerouslySetInnerHTML={{ __html: formatInline(trimmed) }}
        />
      );
    }
  }

  flushList();
  flushBlockquote();
  flushChecklist();
  flushInfobox();
  flushHighlight();
  flushFaq();
  if (inTable) flushTable();

  return elements;
}

// Component for article reference links
function ArticleLink({ title, prefix }: { title: string; prefix: string }) {
  // Comprehensive map of article titles to slugs - must match database exactly
  const slugMap: Record<string, string> = {
    // === Grundlæggende / Intro ===
    "Hvad er et sabbatår?": "hvad-er-et-sabbataar-guide",
    "Hvad er et sabbatår? En guide til dig der overvejer en pause": "hvad-er-et-sabbataar-guide",
    "Sabbatår – Den komplette guide (2026)": "sabbataar-komplet-guide-2026",
    
    // === Planlægning & økonomi ===
    "Budget for sabbatår – hvad koster det egentlig?": "budget-sabbataar-hvad-koster-det",
    "Planlæg dit sabbatår – trin for trin": "planlaeg-sabbataar-trin-for-trin",
    "Sabbatår måned-for-måned: Eksempel på planlægning": "sabbataar-maaned-for-maaned",
    "Det perfekte sabbatår – sådan planlægger du det": "det-perfekte-sabbataar-planlaegning",
    "Er sabbatår med rejse for dyrt? Budget og spareråd": "sabbataar-rejse-budget-sparerad",
    "Legater og tilskud til sabbatårsrejser – hvor kan man søge?": "legater-tilskud-sabbataar",
    
    // === Rejse & udland ===
    "Backpacking i sabbatåret – guide til rute, budget og gear": "backpacking-i-sabbataar",
    "Working Holiday i Australien, New Zealand eller Canada": "working-holiday-australien-new-zealand-canada",
    "Grupperejse i sabbatåret – fordele og ulemper": "grupperejse-i-sabbataar",
    "Sabbatår i udlandet: Her er dine muligheder": "sabbataar-i-udlandet",
    "Sabbatår i Asien, Afrika eller Sydamerika – hvad passer til dig?": "sabbataar-asien-afrika-sydamerika",
    "Sabbatår i Asien, Afrika eller Sydamerika": "sabbataar-asien-afrika-sydamerika",
    "Sabbatår uden fly – bæredygtige rejsemuligheder": "sabbataar-uden-fly-baeredygtige-rejsemuligheder",
    
    // === Arbejde ===
    "Arbejde i udlandet i sabbatåret – hvad kræver det?": "arbejde-i-udlandet-sabbataar",
    "Kombinér job og rejse – sådan finansierer du sabbatåret": "kombiner-job-og-rejse",
    "Job i sabbatåret – her er mulighederne for unge": "job-i-sabbataaret",
    "Skal du arbejde hele sabbatåret? Fordele og faldgruber": "arbejde-hele-sabbataret",
    "Hvordan tjener man penge i sabbatåret uden SU?": "tjen-penge-sabbataar-uden-su",
    
    // === Frivillighed ===
    "Frivilligt arbejde i udlandet – sådan kommer du afsted": "frivilligt-arbejde-i-udlandet",
    "Find frivilligt arbejde i Danmark – og gør en forskel": "frivilligt-arbejde-danmark",
    "Frivilligt arbejde og kvote 2 – derfor tæller det positivt": "frivilligt-arbejde-kvote-2",
    
    // === Højskole ===
    "Højskole i udlandet – kombinér oplevelse og dannelse": "hoejskole-i-udlandet",
    "Derfor vælger tusindvis højskole i sabbatåret": "derfor-vaelger-tusindvis-hoejskole",
    "Højskole vs. rejse – hvad giver mest mening for dig?": "hoejskole-vs-rejse",
    "Hvilken højskole skal jeg vælge? 5 typer og hvad de tilbyder": "hvilken-hoejskole-skal-jeg-vaelge",
    "Hvordan styrker højskole dit CV og kvote 2-ansøgning?": "hoejskole-cv-kvote-2",
    "Pitstop på højskole – for dig der har brug for en pause under studiet": "pitstop-paa-hoejskole",
    
    // === Kvote 2, SU & uddannelse ===
    "Sabbatår og kvote 2 – sådan booster du din ansøgning": "sabbataar-kvote-2-boost-ansoegning",
    "Sabbatår og SU – her er reglerne du skal kende": "sabbataar-og-su-regler",
    "Kan man tage suppleringsfag (GSK) i sabbatåret?": "gsk-suppleringsfag-sabbataar",
    "Kan man tage suppleringsfag i sabbatåret?": "gsk-suppleringsfag-sabbataar",
    "Sabbatår og studievalg – sådan får du afklaring": "sabbataar-studievalg-afklaring",
    "Sabbatår eller studiestart? Sådan træffer du det rigtige valg": "sabbataar-eller-studiestart",
    "Sådan bruger du sabbatåret til at finde den rigtige uddannelse": "sabbataar-find-rigtige-uddannelse",
    "Forbereder du dig til uni? Her er 5 sabbatårsaktiviteter der gavner": "forbered-til-uni-5-sabbatarsaktiviteter",
    "Sabbatår og skat – hvad du skal vide hvis du arbejder": "sabbataar-skat-arbejde",
    "Kan man få dagpenge i sabbatåret? Fakta og alternativer": "dagpenge-sabbataar-fakta-alternativer",
    "Hvordan skriver man sabbatåret på CV'et eller i en ansøgning?": "sabbataar-paa-cv-ansoegning",
    
    // === Idéer & inspiration ===
    "20+ idéer til dit sabbatår – den ultimative guide (2026)": "ideer-til-sabbataar-ultimativ-guide",
    "De mest populære sabbatårsaktiviteter i 2026": "populaere-sabbataarsaktiviteter-2026",
    "10 idéer til dit sabbatår – fra eventyr til erfaring": "10-ideer-til-dit-sabbataar",
    "Hvad skal jeg lave i mit sabbatår? 10 konkrete idéer": "hvad-skal-jeg-lave-i-mit-sabbataar-10-ideer",
    
    // === Refleksion & tvivl ===
    "Bekymret over sabbatår? Her er hvorfor det (oftest) er en god idé": "bekymret-over-sabbataar-god-ide",
    "Kan man fortryde sabbatår? Hvad du skal vide før du vælger": "kan-man-fortryde-sabbataar",
    "Kan man fortryde sabbatår?": "kan-man-fortryde-sabbataar",
    "Er sabbatår spild af tid – eller den bedste beslutning?": "sabbataar-spild-af-tid-eller-bedste-beslutning",
    "Hvor mange sabbatår kan man tage – og hvad er konsekvenserne?": "hvor-mange-sabbataar-kan-man-tage",
    "Tager alle sabbatår? Statistik og trends blandt unge": "sabbataar-statistik-og-trends",
    "Erfaringer fra sabbatår: Hvad jeg lærte (og ville ønske jeg vidste før)": "erfaringer-fra-sabbataar",
    
    // === For forældre ===
    "Guide til forældre: Sådan støtter du dit barn i sabbatåret": "guide-til-foraeldre-stoette-sabbataar",
    "Hvordan taler man med sit barn om at få noget ud af sabbatåret?": "tal-med-barn-om-sabbataar",
    "Dit barn er hjemme fra sabbatår – hvad nu?": "dit-barn-er-hjemme-fra-sabbataar-hvad-nu",
    "Når dit barn vil droppe uddannelse for sabbatår – hvad gør du?": "naar-dit-barn-vil-droppe-uddannelse-for-sabbataar",
    "Økonomisk støtte til dit barns sabbatår – hvad kan du gøre?": "oekonomisk-stoette-til-dit-barns-sabbataar",
    
    // === Selvudvikling ===
    "Sabbatår som selvudvikling": "sabbataar-som-selvudvikling",
    
    // === Ski & vintersport ===
    "Ski guide i sabbatåret – job, sæson og destinationer": "ski-guide-i-sabbataaret",
    "Skisæson i Alperne – job, resorts og praktisk guide": "skisaeson-i-alperne",
    "Ski instruktør certifikat – alt du skal vide om BASI, CSIA og andre": "ski-instruktoer-certifikat-guide",
    "De bedste ski resorts for sabbatår – top 10 worldwide": "bedste-ski-resorts-sabbataar",
  };

  // Find matching slug with improved logic
  let slug = "";
  
  // 1. Try exact match first
  if (slugMap[title]) {
    slug = slugMap[title];
  }
  
  // 2. Try case-insensitive exact match
  if (!slug) {
    const lowerTitle = title.toLowerCase();
    for (const [key, value] of Object.entries(slugMap)) {
      if (key.toLowerCase() === lowerTitle) {
        slug = value;
        break;
      }
    }
  }
  
  // 3. Try contains match (title contains key or key contains title)
  if (!slug) {
    const lowerTitle = title.toLowerCase();
    for (const [key, value] of Object.entries(slugMap)) {
      const lowerKey = key.toLowerCase();
      // Check if one contains the other (with minimum length requirement)
      if (lowerKey.includes(lowerTitle) && lowerTitle.length > 10) {
        slug = value;
        break;
      }
      if (lowerTitle.includes(lowerKey) && lowerKey.length > 10) {
        slug = value;
        break;
      }
    }
  }

  // Log warning in development if no match found
  if (!slug && typeof window !== 'undefined' && window.location.hostname === 'localhost') {
    console.warn(`ArticleLink: No slug found for title "${title}"`);
  }

  if (slug) {
    return (
      <Link
        to={`/artikel/${slug}`}
        className="inline-flex items-center gap-2 text-primary hover:underline font-medium group"
      >
        <span className="text-lg">🔗</span>
        <span>{prefix}: {title}</span>
        <svg
          className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </Link>
    );
  }

  return (
    <span className="text-muted-foreground">
      <span className="text-lg">🔗</span> {prefix}: {title}
    </span>
  );
}

export default ArticleContent;
